// Shared Excel export for the Audit Workflow dashboard drill-down detail rows.
// Used by both the dashboard header button and the drill-down popup so the two
// stay in lockstep. Exports the same row-level "job" data the popup table shows,
// fetching EVERY page for each bucket (the popup only shows 20 at a time), with the
// current dropdown filters applied.
import axios from "axios";
import * as XLSX from "xlsx";
import { apiUrl } from "../../constant";

const PER_PAGE = 200; // backend caps per_page at 200 (AuditDashboardController::drillDown)

const BASE_COLUMNS = ["Job ID", "Client", "Sub Client", "Assigned To"];
const TAIL_COLUMNS = ["Job Type", "Status", "Assign Date", "Due Date", "Remarks"];

function columnsFor(isPhaseMode) {
  return isPhaseMode
    ? [...BASE_COLUMNS, "Phase", ...TAIL_COLUMNS]
    : [...BASE_COLUMNS, ...TAIL_COLUMNS];
}

function fmtDate(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toISOString().slice(0, 10);
}

// Map a raw drill-down row to a flat, human-labelled export row (mirrors the
// popup table columns in AuditDrillDownModal.js).
function toExportRow(r, isPhaseMode) {
  const row = {
    "Job ID": r.task_id ?? "",
    "Client": r.client_name ?? "",
    "Sub Client": r.sub_client_name ?? "",
    "Assigned To": r.assigned_to ?? "",
  };
  if (isPhaseMode) row["Phase"] = r.phase_name ?? "";
  row["Job Type"] = r.job_type ?? "";
  row["Status"] = r.status_name ?? "";
  row["Assign Date"] = fmtDate(r.assigned_date);
  row["Due Date"] = fmtDate(r.due_date);
  row["Remarks"] = r.remarks ?? "";
  return row;
}

// Fetch every page of the drill-down for one bucket, honouring the shared filters.
async function fetchAllRowsForBucket(authHeader, baseParams, bucket) {
  const rows = [];
  let page = 1;
  let lastPage = 1;
  do {
    const res = await axios.get(`${apiUrl}/audit-dashboard/drilldown`, {
      headers: authHeader,
      params: { ...baseParams, bucket, page, per_page: PER_PAGE },
    });
    const body = res.data || {};
    const data = body.data ?? [];
    rows.push(...data);
    lastPage = body.last_page ?? Math.max(1, Math.ceil((body.total ?? data.length) / PER_PAGE));
    page += 1;
  } while (page <= lastPage);
  return rows;
}

// Fetch all detail rows across the given buckets, build a single-sheet workbook,
// and trigger a download. Returns the number of rows exported.
export async function exportAuditDrilldownExcel({
  authHeader,
  baseParams = {},
  buckets,
  isPhaseMode = false,
  filenameLabel = "export",
}) {
  const raw = [];
  for (const bucket of buckets) {
    // Sequential to keep server load modest; each bucket loops its own pages.
    // eslint-disable-next-line no-await-in-loop
    const rows = await fetchAllRowsForBucket(authHeader, baseParams, bucket);
    raw.push(...rows);
  }

  const exportRows = raw.map((r) => toExportRow(r, isPhaseMode));
  const ws = XLSX.utils.json_to_sheet(exportRows, { header: columnsFor(isPhaseMode) });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Jobs");
  XLSX.writeFile(wb, `audit-dashboard-${filenameLabel}-${Date.now()}.xlsx`);
  return exportRows.length;
}
